import { useState, useEffect, useRef, useCallback } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseAutoSaveOptions {
  onSave: () => void | Promise<void>;
  value: string;
  initialValue: string;
  delay?: number;
  enabled?: boolean;
}

interface UseAutoSaveReturn {
  status: SaveStatus;
  lastSavedAt: Date | null;
  triggerSave: () => void;
}

/**
 * Custom hook for auto-saving content with debouncing
 * 
 * @param onSave - Function to call when saving
 * @param value - Current value to save
 * @param initialValue - Initial value to compare against (prevents unnecessary saves)
 * @param delay - Debounce delay in milliseconds (default: 1500ms)
 * @param enabled - Whether auto-save is enabled (default: true)
 */
export const useAutoSave = ({
  onSave,
  value,
  initialValue,
  delay = 1500,
  enabled = true,
}: UseAutoSaveOptions): UseAutoSaveReturn => {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedValueRef = useRef<string>(initialValue);

  // Clear any pending timeout
  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Trigger save function
  const triggerSave = useCallback(async () => {
    // Clear any pending auto-save
    clearPendingTimeout();

    // Don't save if value hasn't changed from last saved value
    if (value === lastSavedValueRef.current) {
      return;
    }

    // Don't save empty values
    if (!value.trim()) {
      return;
    }

    try {
      setStatus('saving');
      await onSave();
      lastSavedValueRef.current = value;
      setStatus('saved');
      setLastSavedAt(new Date());
    } catch (error) {
      console.error('Auto-save error:', error);
      setStatus('error');
    }
  }, [value, onSave, clearPendingTimeout]);

  // Auto-save with debouncing
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Don't set up auto-save if value hasn't changed
    if (value === lastSavedValueRef.current) {
      return;
    }

    // Clear existing timeout
    clearPendingTimeout();

    // Set up new timeout for debounced save
    timeoutRef.current = setTimeout(() => {
      triggerSave();
    }, delay);

    // Cleanup on unmount or value change
    return () => {
      clearPendingTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled, delay]);

  // Update last saved value when initial value changes (e.g., segment switch)
  useEffect(() => {
    lastSavedValueRef.current = initialValue;
    // Reset status when initial value changes
    if (initialValue === value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus('idle');
    }
  }, [initialValue, value]);

  return {
    status,
    lastSavedAt,
    triggerSave,
  };
};
